import React from 'react'
import { Box } from '@mui/material'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useDroppable } from '@dnd-kit/core'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { TeamParticipantCard } from './TeamParticipantCard'
import { TeamValidationTooltip } from '../TeamValidationTooltip/TeamValidationTooltip'
import { EmptySlotPlaceholder } from './EmptySlotPlaceholder'
import { TeamLanguageSelector } from '../TeamLanguageSelector/TeamLanguageSelector'
import { TESTIDS } from '../../../../../config/testids'
import { Team } from '../../types'
import { ParticipantsContainerMode } from '../ParticipantsContainer/ParticipantsContainer'
import {
  createEmptyTeamSlots,
  hasInsufficientTeamMembers,
  getTeamMemberCount,
  getSlotsNeededForMinimum
} from './teamHelpers'

export interface TeamBoxInfoProps {
  team: Team
  onRemoveTeam: (teamId: string) => void
  onRemoveParticipant: (participantId: string) => void
  onDropParticipant?: (participantId: string, teamId: string) => void
  onUpdateTeamLanguage?: (teamId: string, language: string) => void
  mode: ParticipantsContainerMode
  hasValidationErrors?: boolean
  className?: string
}

export const TeamBoxInfo: React.FC<TeamBoxInfoProps> = ({
  team,
  onRemoveTeam,
  onRemoveParticipant,
  onDropParticipant,
  onUpdateTeamLanguage,
  mode,
  hasValidationErrors = false,
  className = ''
}) => {
  const { t } = useTranslation()
  const hasLanguageError = hasValidationErrors && !team.language && mode !== 'preview'
  const { isOver, setNodeRef } = useDroppable({
    id: `team-${team.id}`,
    data: {
      type: 'team',
      teamId: team.id,
      onDrop: onDropParticipant
    },
    disabled: mode === 'preview' // Disable drag/drop in preview mode
  })

  // Use helper functions
  const hasInsufficientMembers = hasInsufficientTeamMembers(team)
  const teamMemberCount = getTeamMemberCount(team)
  const emptySlots = createEmptyTeamSlots(team)

  // Debug logging

  const handleRemoveTeam = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onRemoveTeam(team.id)
  }

  const handleLanguageChange = (language: string) => {
    if (onUpdateTeamLanguage) {
      onUpdateTeamLanguage(team.id, language)
    }
  }

  return (
    <Box
      ref={setNodeRef}
      sx={{
        p: 2,
        borderRadius: 2,
        border: 1,
        borderColor: INTERAX_COLORS.heritageBlue,
        backgroundColor: isOver ? INTERAX_COLORS.heritageBlue : INTERAX_COLORS.heritageBlue40,
        transition: 'background-color 0.2s'
      }}
      className={className}
    >
      {/* Team header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box
          component="h3"
          className="text-headline-5"
          sx={{
            color: INTERAX_COLORS.white,
            fontWeight: 600
          }}
        >
          {team.name}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Language Selector */}
          <TeamLanguageSelector
            value={team.language}
            onChange={handleLanguageChange}
            disabled={mode === 'preview'}
            hasError={hasLanguageError}
            ariaLabel={t('facilitator.trainingSprint.addParticipants.languageSelector.teamAriaLabel', {
              teamName: team.name
            })}
            dataTestId={`${TESTIDS.SPRINT_TEAM_LANG_TRIGGER}-${team.id}`}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasInsufficientMembers && (
              <TeamValidationTooltip message={t('facilitator.trainingSprint.validation.insufficientTeamMembers')} />
            )}
            <Box
              className="text-body-3"
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: '50px',
                backgroundColor: hasInsufficientMembers ? INTERAX_COLORS.redErrorBackground : INTERAX_COLORS.sun,
                color: hasInsufficientMembers ? INTERAX_COLORS.redError : INTERAX_COLORS.offBlack,
                fontWeight: 500
              }}
            >
              {teamMemberCount}
            </Box>
          </Box>

          {/* Only show X button in creation and edit modes */}
          {mode !== 'preview' && (
            <Box
              component="button"
              onClick={handleRemoveTeam}
              aria-label={t('facilitator.trainingSprint.addParticipants.removeTeam', 'Remove team')}
              data-testid={`${TESTIDS.SPRINT_TEAM_REMOVE_BTN}-${team.id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: INTERAX_COLORS.redErrorBackground,
                border: 'none',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              <XMarkIcon className="w-4 h-4" style={{ color: INTERAX_COLORS.redError }} />
            </Box>
          )}
        </Box>
      </Box>

      {hasLanguageError && (
        <Box
          className="text-body-3"
          sx={{
            color: INTERAX_COLORS.redError,
            mb: 2
          }}
        >
          {t('facilitator.trainingSprint.addParticipants.languageSelector.teamRequired')}
        </Box>
      )}

      {/* Participants and empty slots */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {team.participants.map((participant) => (
          <TeamParticipantCard
            key={participant.id}
            participant={participant}
            onRemove={onRemoveParticipant}
            showRemoveButton={mode !== 'preview'}
            mode={mode}
          />
        ))}
        {emptySlots.map((slot, index) => {
          // Only mark the slots needed to reach minimum as red
          const slotsNeeded = getSlotsNeededForMinimum(team)
          const needsAttention = hasValidationErrors && hasInsufficientMembers && index < slotsNeeded

          return <EmptySlotPlaceholder key={slot.key} needsAttention={needsAttention} />
        })}
      </Box>
    </Box>
  )
}
